import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CalendarDaysIcon, ChatBubbleLeftEllipsisIcon, ChevronDownIcon, ClipboardDocumentCheckIcon } from "@heroicons/react/24/outline";
import { CheckIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { getMyTasks } from "../../../services/questionService";
import { isCanceledError } from "../../../utils/examError";
import { DifficultyBadge } from "../../Difficulty/Difficulty";
import { focusRing } from "../matrix/MatrixParts";
import { gradeLabel, subtopicsOf, trimName } from "../matrix/matrixModel";
import { formatDue, writeLevels } from "./taskModel";

// The author's write tasks in work in the subject the questions are written in, `taskUuid` (the task's "Write a
// question") first and alone when it is one of them. [] while they are read, without a subject, or when they fail.
function useWriteTasks(folderUuid, taskUuid) {
  const [tasks, setTasks] = useState([]);
  useEffect(() => {
    setTasks([]);
    if (!folderUuid) return undefined;
    const controller = new AbortController();
    getMyTasks({ done: false, signal: controller.signal })
      .then((data) => {
        const list = (Array.isArray(data) ? data : data?.results || []).filter(
          (task) => task.kind === "write" && !task.cancelled && task.folder?.uuid === folderUuid
        );
        const chosen = taskUuid && list.find((task) => task.uuid === taskUuid);
        setTasks(chosen ? [chosen] : list);
      })
      .catch((err) => {
        if (!isCanceledError(err)) setTasks([]);
      });
    return () => controller.abort();
  }, [folderUuid, taskUuid]);
  return tasks;
}

function Rule({ ok, children }) {
  const Icon = ok ? CheckIcon : XMarkIcon;
  return (
    <li className="flex items-start gap-2">
      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${ok ? "text-emerald-600" : "text-red-500"}`} aria-hidden="true" />
      <span className="min-w-0">{children}</span>
    </li>
  );
}

// One task: what to write (topic, subtopics, grade), how many of each level and how many are approved, its due date
// and the moderator's comment.
function TaskWhat({ task }) {
  const [t, i18n] = useTranslation();
  const subtopics = subtopicsOf(task).map((s) => trimName(s.name));
  return (
    <div className="min-w-0 space-y-2">
      <p className="text-sm text-slate-900">
        <span className="font-semibold">{trimName(task.topic?.name)}</span>
        {task.topic && <span className="text-slate-500"> · {gradeLabel(t, task.topic.grade)}</span>}
      </p>
      <p className="text-sm text-slate-700">
        <span className="text-slate-500">{t("folders.tasks.reminder.subtopics")} </span>
        {subtopics.length ? subtopics.join("; ") : t("folders.tasks.reminder.any_subtopic")}
      </p>
      <ul className="flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-slate-700" aria-label={t("folders.tasks.reminder.levels")}>
        {writeLevels(task).map(({ level, planned, approved }) => (
          <li key={level} className="inline-flex items-center gap-1.5 tabular-nums">
            <DifficultyBadge level={level} showPoints={false} />
            {t("folders.tasks.reminder.level_count", { planned, approved })}
          </li>
        ))}
      </ul>
      {(task.due_date || task.comment) && (
        <div className="space-y-1 text-sm text-slate-600">
          {task.due_date && (
            <p className={`flex items-center gap-1.5 ${task.overdue ? "font-medium text-red-700" : ""}`}>
              <CalendarDaysIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
              {t("folders.tasks.due_on", { date: formatDue(task.due_date, i18n.language) })}
            </p>
          )}
          {task.comment && (
            <p className="flex items-start gap-1.5 whitespace-pre-line break-words">
              <ChatBubbleLeftEllipsisIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
              <span className="min-w-0">{task.comment}</span>
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The create page's reminder of the author's task (TZ 2.2), at hand on every step, folded or not: what to write
 * (each write task in work in the subject, `taskUuid` alone when the page was opened from it) and what counts for it
 * and what does not. Nothing without a task.
 */
export default function WriteTaskReminder({ folderUuid, taskUuid = null }) {
  const [t] = useTranslation();
  const tasks = useWriteTasks(folderUuid, taskUuid);
  const [open, setOpen] = useState(true);
  if (!tasks.length) return null;
  const finish = t("my_questions.actions.finish_and_create");
  return (
    <section aria-labelledby="task-reminder-title" className="shrink-0 rounded-2xl border border-indigo-200 bg-indigo-50/50 shadow-sm">
      <h2 className="m-0">
        <button
          type="button"
          id="task-reminder-title"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
          className={`flex w-full items-center gap-2 rounded-2xl px-4 py-3 text-left text-sm font-semibold text-indigo-950 sm:px-5 ${focusRing}`}
        >
          <ClipboardDocumentCheckIcon className="h-5 w-5 shrink-0 text-indigo-600" aria-hidden="true" />
          <span className="min-w-0 flex-1">{t("folders.tasks.reminder.title", { count: tasks.length })}</span>
          <span className="shrink-0 text-xs font-medium text-indigo-700">{open ? t("folders.tasks.reminder.hide") : t("folders.tasks.reminder.show")}</span>
          <ChevronDownIcon className={`h-4 w-4 shrink-0 text-indigo-600 transition duration-200 ${open ? "rotate-180" : ""}`} aria-hidden="true" />
        </button>
      </h2>
      {open && (
        <div className="grid gap-4 border-t border-indigo-100 px-4 pb-4 pt-3 sm:px-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <div className="space-y-4">
            {tasks.map((task) => (
              <TaskWhat key={task.uuid} task={task} />
            ))}
          </div>
          <div className="rounded-xl bg-white px-4 py-3 ring-1 ring-inset ring-indigo-100">
            <p className="text-sm font-semibold text-slate-900">{t("folders.tasks.reminder.rules_title")}</p>
            <ul className="mt-2 space-y-1.5 text-sm text-slate-700">
              <Rule ok>{t("folders.tasks.reminder.rule_topic")}</Rule>
              <Rule ok>{t("folders.tasks.reminder.rule_level")}</Rule>
              <Rule ok>{t("folders.tasks.reminder.rule_send", { finish })}</Rule>
              <Rule ok>{t("folders.tasks.reminder.rule_approved")}</Rule>
              <Rule ok={false}>{t("folders.tasks.reminder.rule_not_counted")}</Rule>
            </ul>
          </div>
        </div>
      )}
    </section>
  );
}
